// Workflow Report Step - Generate final credit analysis report
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  FileText,
  Sparkles,
  Eye,
  CheckCircle,
  FileBarChart,
  CreditCard,
  Banknote,
  Building2,
  Calculator,
  Save,
  Pencil,
  Loader2,
} from 'lucide-react';
import { SaveTemplateDialog } from '@/components/templates';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api-client';
import { Badge } from '@/components/ui/badge';
import { getWriteupType } from '@/lib/writeup-type-util';

export const Route = createFileRoute('/workflow/_layout/report')({
  component: ReportStep,
});

function ReportStep() {
  const { setCurrentStep, data, updateData, resetWorkflow, markStepComplete } = useWorkflow();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [businessName, setBusinessName] = useState((data.businessName || '').trim());
  const [isEditingName, setIsEditingName] = useState(false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingPhase, setGeneratingPhase] = useState<string>('');
  const [reportId, setReportId] = useState<string | null>(data.reportId || null);

    // Summary of collected data
  const dataPoints = [
    {
      icon: FileBarChart,
      label: 'Financial Statement',
      available: !!data.financialData,
      color: 'text-blue-500',
    },
    {
      icon: Calculator,
      label: 'Financial Metrics',
      available: data.selectedMetrics.length > 0,
      color: 'text-cyan-500',
    },
    {
      icon: CreditCard,
      label: 'Credit Report',
      available: !!data.creditReportData,
      color: 'text-purple-500',
    },
    {
      icon: Banknote,
      label: 'Bank Statements',
      available: !!data.bankStatementFindings,
      color: 'text-green-500',
    },
    {
      icon: Building2,
      label: 'Corporate Search',
      available: !!data.accountData,
      color: 'text-orange-500',
    },
  ];

  const availableDataCount = dataPoints.filter((d) => d.available).length;

  const isTemplateSession = !!data.templateId;

  // Sync workflow context
  useEffect(() => {
    setCurrentStep('report');
    if (!businessName) {
      // Report name resolution priority:
      // 1. User-provided reportName from summary preferences (set on the preferences page)
      // 2. Company name extracted from financial data
      // 3. Subject name from credit report
      // 4. Account holder from bank statement
      // 5. Company name from corporate search
      const userReportName = data.summaryPreferences?.reportName;
      const financialName = data.financialData?.companyName;
      const creditReportName = data.creditReportData?.subjectName || (data.creditReportData as any)?.companyName;
      const bankName = data.bankStatementFindings?.accountInfo?.accountHolder || (data.bankStatementFindings as any)?.companyName;
      const corporateName = data.accountData?.companyName || (data.accountData as any)?.companyName;

      const preferredName = userReportName || financialName || creditReportName || bankName || corporateName || '';

      if (preferredName) {
        setBusinessName(preferredName.trim());
      }
    }
  }, [setCurrentStep, data, businessName]);

  const handleGenerateReport = async () => {
    if (!businessName.trim()) {
      toast({
        title: 'Business Name Required',
        description: 'Please enter the business name before generating the report',
        variant: 'destructive',
      });
      return;
    }
    // Lock name edit as soon as user clicks on generate report
    setIsEditingName(false);
    // sync workflow data
    updateData({ businessName: businessName.trim(), workflowState: 'generating' });
    // Sync workflow state to backend (fire-and-forget)
    if (data.workflowId) {
      api.workflows.update(data.workflowId, { workflowState: 'generating' }).catch((err) =>
        console.warn('Failed to sync workflowState=generating to backend:', err)
      );
    }
    setIsGenerating(true);
    setGeneratingPhase('Preparing data for AI agents...');

    const computedWriteup = getWriteupType(data);

    try {
      setGeneratingPhase('AI agents synthesizing credit narrative...');

      const result = await api.reports.generate({
        workflowId: data.workflowId,
        businessName,
        writeupType: computedWriteup.templateType,
        financialData: data.financialData,
        selectedMetrics: data.selectedMetrics,
        customRatios: data.customRatios,
        offBalanceSheetRecords: data.offBalanceSheetRecords,
        creditReportData: data.creditReportData,
        selectedCreditReportItems: data.selectedCreditReportItems,
        accountData: data.accountData,
        bankStatementFindings: data.bankStatementFindings,
        summaryPreferences: data.summaryPreferences,
        // Document IDs for tracking
        financialUploadId: data.financialUploadId,
        creditReportUploadId: data.creditReportUploadId,
        bankStatementUploadId: data.bankStatementUploadId,
        corporateUploadId: data.corporateUploadId,
        // Cross-period analysis results (multi-document trend data)
        crossPeriodAnalysis: data.crossPeriodAnalysis,
        // Multi-document financial extractions (if available from concurrent workflow)
        allFinancialExtractions: data.financialExtractions.length > 0
          ? data.financialExtractions.map((e) => ({
              data: e.data,
              period: e.period,
              documentId: e.documentId,
            }))
          : undefined,
      });

      setGeneratingPhase('Finalizing report...');

      // Extract reportId from the response - API returns { success, data: { reportId, ... } }
      const generatedReportId = result.data?.reportId || result.reportId;

      // Update local state AND Global Context
      // This 'unlocks' the Navigation Guard in _layout.tsx
      setReportId(generatedReportId);
      updateData({ reportId: generatedReportId, workflowState: 'complete' });
      // Sync workflow state to backend (fire-and-forget)
      if (data.workflowId) {
        api.workflows.update(data.workflowId, { workflowState: 'complete' }).catch((err) =>
          console.warn('Failed to sync workflowState=complete to backend:', err)
        );
      }
      markStepComplete('report');

      // Storing the report in sessionStorage for quick access when navigating to the report view
      // So that it can be used directly to save as a template without making another api call
      try {
        const reportPayload = result.data || result;
        if (generatedReportId && reportPayload) {
          sessionStorage.setItem(`analysis:${generatedReportId}`, JSON.stringify(reportPayload));
        }
      } catch (err) {
        console.warn('Session storage failed', err);
      }

      toast({
        title: 'Report Generated',
        description: 'Your credit analysis report is ready to view',
      });
    } catch (error: any) {
      const isTrackingError = error.status === 503 || error.message?.toLowerCase().includes('billing');
      const isTimeout = error.status === 504 || error.message?.toLowerCase().includes('timeout');

      toast({
        title: isTrackingError ? 'System Busy' : 'Generation Failed',
        description: isTrackingError
          ? 'The usage tracking system is temporarily unavailable. Please try again in 30 seconds.'
          : isTimeout
          ? 'The AI synthesis is taking longer than usual. Please try again.'
          : error.message || 'Failed to generate report',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
      setGeneratingPhase('');
    }
  };

  const handleViewReport = () => {
    if (reportId) {
      navigate({ to: '/analyses/$id', params: { id: reportId } });
    }
  };

  const handleNewAnalysis = () => {
    resetWorkflow();
    navigate({ to: '/workflow' });
  };

  return (
    <div className="max-w-5xl mx-auto">
      {isTemplateSession && !reportId && (
      <div className="flex justify-center mb-6 animate-in slide-in-from-top-4 duration-500">
        <Badge variant="secondary" className="px-4 py-1.5 gap-2 bg-accent-500/10 text-accent-600 border-accent-500/20 shadow-sm">
          <Sparkles className="w-3.5 h-3.5" />
          Template Applied: Review & Generate
        </Badge>
      </div>
    )}
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <FileText className="w-10 h-10 text-accent-500" />
          <h1 className="text-4xl font-bold">Generate Credit Report</h1>
        </div>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Review your collected data and generate a comprehensive credit analysis report.
        </p>
      </div>

      {/* Business Information Card */}
      <div className="space-y-4">
        <Card className="p-4 border-2 rounded-2xl">
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="business-name" className="text-base font-semibold block">
                  Business Name *
                </Label>
                {!reportId && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 text-accent-600 hover:text-accent-700"
                    disabled={isGenerating}
                    onClick={() => {
                      if (isEditingName) {
                        setBusinessName(businessName.trim());
                        updateData({ businessName: businessName.trim() });
                      }
                      setIsEditingName(!isEditingName);
                    }}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" />
                    {isEditingName ? 'Lock Name' : 'Edit Name'}
                  </Button>
                )}
              </div>
              <div className="relative">
                <Input
                  id="business-name"
                  placeholder="Enter business name"
                  value={businessName}
                  disabled={!isEditingName || !!reportId}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className={cn(
                    "border-2 rounded-xl pr-10 transition-all",
                    !isEditingName && "bg-muted/30 border-transparent cursor-not-allowed"
                  )}
                />
                {(!!reportId || (!isEditingName && businessName)) && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>
              {!isEditingName && (
                <p className="text-[10px] text-muted-foreground mt-1.5 ml-1">
                  Name is locked based on extracted data. Click Edit to change.
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>

        <div className="space-y-8 mt-4">
        {/* Data Summary Card */}
        <Card className="p-8 border-2 rounded-2xl">
          <h3 className="text-xl font-semibold mb-6">Data Collection Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {dataPoints.map((point) => {
              const Icon = point.icon;
              return (
                <div
                  key={point.label}
                  className={cn(
                    'p-4 rounded-xl border-2 text-center transition-colors',
                    point.available
                      ? 'border-green-500/30 bg-green-500/5'
                      : 'border-border bg-muted/50'
                  )}
                >
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center',
                      point.available ? 'bg-green-500/10' : 'bg-muted'
                    )}
                  >
                    {point.available ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Icon className={cn('w-5 h-5', point.color, 'opacity-50')} />
                    )}
                  </div>
                  <p
                    className={cn(
                      'text-sm font-medium',
                      point.available ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {point.label}
                  </p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-muted-foreground mt-4">
            {availableDataCount} of {dataPoints.length} data sources collected
          </p>
        </Card>

        {/* Generating Status Card */}
        {isGenerating && (
          <Card className="p-6 border-2 rounded-2xl border-accent-500/30 bg-accent-500/5">
            <div className="flex items-center gap-4">
              <Loader2 className="w-8 h-8 text-accent-500 animate-spin flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold">Generating Report</h3>
                <p className="text-sm text-muted-foreground mt-1">{generatingPhase}</p>
                <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                  {data.financialData && <p>Financial data from {data.financialExtractions.length || 1} statement{(data.financialExtractions.length || 1) !== 1 ? 's' : ''}</p>}
                  {data.creditReportData && <p>Credit report data included</p>}
                  {data.bankStatementFindings && <p>Bank statement findings included</p>}
                  {data.accountData && <p>Corporate search data included</p>}
                  {data.crossPeriodAnalysis && (data.crossPeriodAnalysis.financial || data.crossPeriodAnalysis.creditReport || data.crossPeriodAnalysis.bankStatement || data.crossPeriodAnalysis.corporate) && (
                    <p className="text-accent-600 dark:text-accent-400 font-medium">
                      Cross-period trend analysis included
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-4">
          {reportId ? (
            <>
              <Button size="lg" onClick={handleViewReport}>
                <Eye className="w-5 h-5 mr-2" />
                View Report
              </Button>
              <SaveTemplateDialog
                workflowData={data}
                trigger={
                  <Button size="lg" variant="outline">
                    <Save className="w-5 h-5 mr-2" />
                    Save as Template
                  </Button>
                }
              />
              <Button size="lg" variant="outline" onClick={handleNewAnalysis}>
                Start New Analysis
              </Button>
            </>
          ) : (
            <Button
              size="lg"
              onClick={handleGenerateReport}
              disabled={isGenerating || availableDataCount === 0}
            >
              {isGenerating ? (
                <>
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Generating Report...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Credit Report
                </>
              )}
            </Button>
          )}
        </div>

        {/* Info Card */}
        {!reportId && (
          <Card className="p-6 border-2 rounded-2xl bg-primary/5">
            <p className="text-center text-muted-foreground">
              <strong>Note:</strong> The AI will generate a comprehensive credit analysis report
              based on your collected data and summary preferences. This may take a few moments.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}